import { useRouter } from 'next/router'
import { useEffect, useState, useCallback, useMemo } from 'react'

import { postContentReview } from '@/legacy/core/api/contentReview'
import { hasWindow } from '@/legacy/core/utils/dom/utils'

import useCookieReview from './useSaveReview'

export const useReviewForm = () => {
  const [rating, setRating] = useState<number>(0)
  const [hasError, setHasError] = useState(false)
  const [success, setSuccess] = useState(false)

  const { saveReview, getReviewByUrl } = useCookieReview()
  const { asPath: pageUrl } = useRouter()

  const reviewAlreadyRegistered = useCallback(() => {
    const previousReview = getReviewByUrl(pageUrl)

    return previousReview === rating
  }, [rating, pageUrl, getReviewByUrl])

  const showSuccess = useMemo(() => {
    if (hasError || !hasWindow()) return false

    return success || !!reviewAlreadyRegistered()
  }, [hasError, success, reviewAlreadyRegistered])

  const submitReview = useCallback(async () => {
    if (reviewAlreadyRegistered()) return

    try {
      setHasError(false)
      setSuccess(false)

      const payload: { url: string; rating: number } = { url: pageUrl, rating }

      const { data } = await postContentReview(payload)

      if (!data?.ok) {
        setHasError(true)
        setSuccess(false)
        return
      }

      saveReview({ rating, url: pageUrl })
      setSuccess(true)
    } catch (error) {
      setHasError(true)
      setSuccess(false)
    }
  }, [rating, pageUrl, saveReview, reviewAlreadyRegistered])

  useEffect(() => {
    const review = getReviewByUrl(pageUrl)
    if (review) setRating(review)
  }, [pageUrl, getReviewByUrl])

  return {
    submitReview,
    hasError,
    showSuccess,
    rating,
    setRating,
  }
}
